/**
 * Modo Forja Context
 * Global state management for the immersive focus mode experience
 * Supports minimization to floating widget while timer/music continue
 */

import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { startSession, endSession, fetchUserSessionsToday } from '@/services/modoforjaMetrics';
import { playStartForjaSound, playCountdownBeep, playSessionEndSound } from '@/lib/modoforjaSounds';
import type { RadioStation, AudioSource } from '@/types/radioStations';

type SessionType = 'focus' | 'break';
type ModoForjaType = 'standard' | 'custom';

interface Track {
  id: string;
  file_name: string;
  file_path: string;
  duration_seconds: number | null;
}

interface ModoForjaState {
  // Overlay
  isOpen: boolean;
  isMinimized: boolean;
  
  // Timer
  timeLeft: number;
  isRunning: boolean;
  sessionType: SessionType;
  
  // Cycles
  focusDuration: number;
  breakDuration: number;
  currentCycle: number;
  totalCycles: number | null; // null = infinite
  
  // Configuration
  mode: ModoForjaType;
  soundEnabled: boolean;
  
  // Stats
  completedSessions: number;
  
  // Radio/Audio (global state)
  isRadioPlaying: boolean;
  volume: number;
  isMuted: boolean;
  tracks: Track[];
  currentTrack: Track | null;
  tracksLoading: boolean;
  
  // Audio source (local music or online radio)
  audioSource: AudioSource;
  stations: RadioStation[];
  currentStation: RadioStation | null;
  stationsLoading: boolean;
}

interface ModoForjaContextValue extends ModoForjaState {
  // Actions
  openModoForja: () => void;
  minimizeModoForja: () => void;
  expandModoForja: () => void;
  endModoForja: () => void;
  toggleTimer: () => void;
  resetTimer: () => void;
  setFocusDuration: (minutes: number) => void;
  setBreakDuration: (minutes: number) => void;
  setTotalCycles: (cycles: number | null) => void;
  setMode: (mode: ModoForjaType) => void;
  toggleSound: () => void;
  // Radio controls
  setRadioPlaying: (playing: boolean) => void;
  toggleRadio: () => void;
  setVolume: (volume: number) => void;
  setMuted: (muted: boolean) => void;
  toggleMute: () => void;
  setTracks: (tracks: Track[]) => void;
  setCurrentTrack: (track: Track | null) => void;
  setTracksLoading: (loading: boolean) => void;
  // Audio source controls
  setAudioSource: (source: AudioSource) => void;
  setStations: (stations: RadioStation[]) => void;
  setCurrentStation: (station: RadioStation | null) => void;
  setStationsLoading: (loading: boolean) => void;
  // Confirmation
  needsEndConfirmation: boolean;
  confirmEnd: () => void;
  cancelEnd: () => void;
}

const DEFAULT_FOCUS_MINUTES = 25;
const DEFAULT_BREAK_MINUTES = 5;
const STORAGE_KEY = 'modo-forja-state';
const SESSION_STORAGE_KEY = 'modo-forja-session';

const ModoForjaContext = createContext<ModoForjaContextValue | undefined>(undefined);

export function ModoForjaProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  
  // Core state
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [timeLeft, setTimeLeft] = useState(DEFAULT_FOCUS_MINUTES * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [sessionType, setSessionType] = useState<SessionType>('focus');
  const [focusDuration, setFocusDurationState] = useState(DEFAULT_FOCUS_MINUTES * 60);
  const [breakDuration, setBreakDurationState] = useState(DEFAULT_BREAK_MINUTES * 60);
  const [currentCycle, setCurrentCycle] = useState(1);
  const [totalCycles, setTotalCyclesState] = useState<number | null>(4);
  const [mode, setModeState] = useState<ModoForjaType>('standard');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [completedSessions, setCompletedSessions] = useState(0);
  const [needsEndConfirmation, setNeedsEndConfirmation] = useState(false);
  
  // Audio state (elevated to context for persistence)
  const [isRadioPlaying, setIsRadioPlaying] = useState(false);
  const [volume, setVolumeState] = useState(0.5);
  const [isMuted, setIsMuted] = useState(false);
  const [tracks, setTracksState] = useState<Track[]>([]);
  const [currentTrack, setCurrentTrackState] = useState<Track | null>(null);
  const [tracksLoading, setTracksLoadingState] = useState(true);
  
  // Audio source state (local music or online radio)
  const [audioSource, setAudioSourceState] = useState<AudioSource>('local');
  const [stations, setStationsState] = useState<RadioStation[]>([]);
  const [currentStation, setCurrentStationState] = useState<RadioStation | null>(null);
  const [stationsLoading, setStationsLoadingState] = useState(true);
  
  // Session tracking refs
  const currentSessionId = useRef<string | null>(null);
  const sessionStartTime = useRef<number | null>(null);
  const warningPlayed = useRef(false);
  const endTimeRef = useRef<number | null>(null);
  const lastBeepRef = useRef<number | null>(null);

  // Load persisted state and sessions count
  useEffect(() => {
    // Load settings from localStorage
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.focusDuration) setFocusDurationState(parsed.focusDuration);
        if (parsed.breakDuration) setBreakDurationState(parsed.breakDuration);
        if (parsed.totalCycles !== undefined) setTotalCyclesState(parsed.totalCycles);
        if (parsed.mode) setModeState(parsed.mode);
        if (parsed.soundEnabled !== undefined) setSoundEnabled(parsed.soundEnabled);
        if (parsed.volume !== undefined) setVolumeState(parsed.volume);
      }
      
      // Restore running session if page was refreshed
      const sessionData = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (sessionData) {
        const session = JSON.parse(sessionData);
        const remaining = Math.ceil((session.endTime - Date.now()) / 1000);
        
        if (remaining > 0 && session.isRunning) {
          setTimeLeft(remaining);
          endTimeRef.current = session.endTime;
          setIsRunning(true);
          setSessionType(session.sessionType);
          setCurrentCycle(session.currentCycle || 1);
          setIsMinimized(true);
          currentSessionId.current = session.sessionId;
          sessionStartTime.current = session.startedAt;
        } else {
          sessionStorage.removeItem(SESSION_STORAGE_KEY);
        }
      }
    } catch (e) {
      console.error('Error loading modo forja state:', e);
    }
    
    // Load completed sessions
    if (user) {
      fetchUserSessionsToday().then(count => {
        setCompletedSessions(count);
      });
    }
  }, [user]);

  // Persist settings to localStorage
  useEffect(() => {
    const state = {
      focusDuration,
      breakDuration,
      totalCycles,
      mode,
      soundEnabled,
      volume,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [focusDuration, breakDuration, totalCycles, mode, soundEnabled, volume]);

  // Persist running session to sessionStorage (survives F5)
  useEffect(() => {
    if (isRunning && endTimeRef.current) {
      const sessionData = {
        isRunning: true,
        sessionType,
        currentCycle,
        endTime: endTimeRef.current,
        startedAt: sessionStartTime.current || Date.now(),
        sessionId: currentSessionId.current,
      };
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionData));
    } else if (!isRunning) {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  }, [isRunning, timeLeft, sessionType, currentCycle]);

  // Timer logic with wall-clock reference (immune to tab throttling)
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isRunning && timeLeft > 0) {
      // Set endTime ref if not already set (first tick after start)
      if (!endTimeRef.current) {
        endTimeRef.current = Date.now() + timeLeft * 1000;
      }

      interval = setInterval(() => {
        const remaining = Math.ceil((endTimeRef.current! - Date.now()) / 1000);
        
        if (remaining <= 0) {
          setTimeLeft(0);
        } else {
          // Countdown beeps at 5, 4, 3, 2, 1 seconds
          if (remaining >= 1 && remaining <= 5 && soundEnabled && lastBeepRef.current !== remaining) {
            lastBeepRef.current = remaining;
            playCountdownBeep(remaining);
          }
          setTimeLeft(remaining);
        }
      }, 250); // Faster polling for smoother updates on tab return
    } else if (timeLeft === 0 && isRunning) {
      handleSessionComplete();
    }

    return () => clearInterval(interval);
  }, [isRunning, timeLeft, soundEnabled]);

  // Force immediate update when tab becomes visible again
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && isRunning && endTimeRef.current) {
        const remaining = Math.ceil((endTimeRef.current - Date.now()) / 1000);
        if (remaining <= 0) {
          setTimeLeft(0);
        } else {
          setTimeLeft(remaining);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [isRunning]);

  const handleSessionComplete = useCallback(async () => {
    warningPlayed.current = false;
    endTimeRef.current = null;
    lastBeepRef.current = null;
    
    // End current session in database
    if (currentSessionId.current && sessionStartTime.current) {
      const elapsedSeconds = Math.floor((Date.now() - sessionStartTime.current) / 1000);
      await endSession(currentSessionId.current, elapsedSeconds, true);
      currentSessionId.current = null;
      sessionStartTime.current = null;
    }

    if (soundEnabled) await playSessionEndSound();

    if (sessionType === 'focus') {
      setCompletedSessions(prev => prev + 1);
      setSessionType('break');
      setTimeLeft(breakDuration);
      setIsRunning(false);
    } else {
      
      // Check if we've completed all cycles
      if (totalCycles !== null && currentCycle >= totalCycles) {
        // All cycles complete
        setIsRunning(false);
        setCurrentCycle(1);
        setSessionType('focus');
        setTimeLeft(focusDuration);
      } else {
        // Start next cycle
        setCurrentCycle(prev => prev + 1);
        setSessionType('focus');
        setTimeLeft(focusDuration);
        setIsRunning(false);
      }
    }
  }, [sessionType, soundEnabled, breakDuration, focusDuration, totalCycles, currentCycle]);

  const openModoForja = useCallback(() => {
    setIsMinimized(false);
    setIsOpen(true);
  }, []);

  const minimizeModoForja = useCallback(() => {
    setIsOpen(false);
    // Only show widget if timer is running or radio is playing
    if (isRunning || isRadioPlaying) {
      setIsMinimized(true);
    } else {
      setIsMinimized(false);
    }
  }, [isRunning, isRadioPlaying]);

  // Auto-show widget when overlay closes but session is still active
  useEffect(() => {
    if (!isOpen && (isRunning || isRadioPlaying) && !isMinimized) {
      setIsMinimized(true);
    }
  }, [isOpen, isRunning, isRadioPlaying, isMinimized]);

  const expandModoForja = useCallback(() => {
    setIsMinimized(false);
    setIsOpen(true);
  }, []);

  const endModoForja = useCallback(() => {
    if (isRunning) {
      setNeedsEndConfirmation(true);
      return;
    }
    // Actually end the session
    performEnd();
  }, [isRunning]);

  const performEnd = useCallback(async () => {
    // End current session in database
    if (currentSessionId.current && sessionStartTime.current) {
      const elapsedSeconds = Math.floor((Date.now() - sessionStartTime.current) / 1000);
      await endSession(currentSessionId.current, elapsedSeconds, false);
      currentSessionId.current = null;
      sessionStartTime.current = null;
    }
    
    warningPlayed.current = false;
    endTimeRef.current = null;
    lastBeepRef.current = null;
    setIsRunning(false);
    setIsOpen(false);
    setIsMinimized(false);
    setIsRadioPlaying(false);
    setNeedsEndConfirmation(false);
    setTimeLeft(focusDuration);
    setSessionType('focus');
    setCurrentCycle(1);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  }, [focusDuration]);

  const confirmEnd = useCallback(() => {
    performEnd();
  }, [performEnd]);

  const cancelEnd = useCallback(() => {
    setNeedsEndConfirmation(false);
  }, []);

  const toggleTimer = useCallback(async () => {
    if (!isRunning) {
      // Starting timer
      if (soundEnabled) await playStartForjaSound();
      
      // Set wall-clock end time
      endTimeRef.current = Date.now() + timeLeft * 1000;
      lastBeepRef.current = null;
      
      if (user) {
        const currentDuration = sessionType === 'focus' ? focusDuration : breakDuration;
        const sessionId = await startSession(sessionType, currentDuration);
        if (sessionId) {
          currentSessionId.current = sessionId;
          sessionStartTime.current = Date.now();
        }
      }
      warningPlayed.current = false;
      setIsRunning(true);
    } else {
      // Pausing timer — snapshot remaining time and clear endTime
      if (endTimeRef.current) {
        const remaining = Math.ceil((endTimeRef.current - Date.now()) / 1000);
        setTimeLeft(Math.max(remaining, 0));
      }
      endTimeRef.current = null;
      
      if (currentSessionId.current && sessionStartTime.current) {
        const elapsedSeconds = Math.floor((Date.now() - sessionStartTime.current) / 1000);
        await endSession(currentSessionId.current, elapsedSeconds, false);
        currentSessionId.current = null;
        sessionStartTime.current = null;
      }
      setIsRunning(false);
    }
  }, [isRunning, user, sessionType, focusDuration, breakDuration, soundEnabled, timeLeft]);

  const resetTimer = useCallback(async () => {
    // End current session if running
    if (currentSessionId.current && sessionStartTime.current) {
      const elapsedSeconds = Math.floor((Date.now() - sessionStartTime.current) / 1000);
      await endSession(currentSessionId.current, elapsedSeconds, false);
      currentSessionId.current = null;
      sessionStartTime.current = null;
    }
    
    warningPlayed.current = false;
    endTimeRef.current = null;
    lastBeepRef.current = null;
    setIsRunning(false);
    setTimeLeft(sessionType === 'focus' ? focusDuration : breakDuration);
  }, [sessionType, focusDuration, breakDuration]);

  const setFocusDuration = useCallback((minutes: number) => {
    const seconds = minutes * 60;
    setFocusDurationState(seconds);
    if (sessionType === 'focus' && !isRunning) {
      setTimeLeft(seconds);
    }
  }, [sessionType, isRunning]);

  const setBreakDuration = useCallback((minutes: number) => {
    const seconds = minutes * 60;
    setBreakDurationState(seconds);
    if (sessionType === 'break' && !isRunning) {
      setTimeLeft(seconds);
    }
  }, [sessionType, isRunning]);

  const setTotalCycles = useCallback((cycles: number | null) => setTotalCyclesState(cycles), []);

  const setMode = useCallback((newMode: ModoForjaType) => {
    setModeState(newMode);
    if (newMode === 'standard') {
      setFocusDurationState(25 * 60);
      setBreakDurationState(5 * 60);
      setTotalCyclesState(4);
      if (!isRunning) {
        setTimeLeft(25 * 60);
        setSessionType('focus');
      }
    }
  }, [isRunning]);

  const toggleSound = useCallback(() => {
    setSoundEnabled(prev => !prev);
  }, []);

  const toggleRadio = useCallback(() => {
    setIsRadioPlaying(prev => !prev);
  }, []);

  const setVolume = useCallback((vol: number) => {
    setVolumeState(vol);
    if (vol > 0 && isMuted) {
      setIsMuted(false);
    }
  }, [isMuted]);

  const setMuted = useCallback((muted: boolean) => {
    setIsMuted(muted);
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => !prev);
  }, []);

  // Direct state setters (no wrapper needed)
  const setTracks = setTracksState;
  const setCurrentTrack = setCurrentTrackState;
  const setTracksLoading = setTracksLoadingState;
  const setAudioSource = setAudioSourceState;
  const setStations = setStationsState;
  const setCurrentStation = setCurrentStationState;
  const setStationsLoading = setStationsLoadingState;

  const value: ModoForjaContextValue = useMemo(() => ({
    isOpen,
    isMinimized,
    timeLeft,
    isRunning,
    sessionType,
    focusDuration,
    breakDuration,
    currentCycle,
    totalCycles,
    mode,
    soundEnabled,
    completedSessions,
    isRadioPlaying,
    volume,
    isMuted,
    tracks,
    currentTrack,
    tracksLoading,
    audioSource,
    stations,
    currentStation,
    stationsLoading,
    needsEndConfirmation,
    openModoForja,
    minimizeModoForja,
    expandModoForja,
    endModoForja,
    toggleTimer,
    resetTimer,
    setFocusDuration,
    setBreakDuration,
    setTotalCycles,
    setMode,
    toggleSound,
    setRadioPlaying: setIsRadioPlaying,
    toggleRadio,
    setVolume,
    setMuted,
    toggleMute,
    setTracks,
    setCurrentTrack,
    setTracksLoading,
    setAudioSource,
    setStations,
    setCurrentStation,
    setStationsLoading,
    confirmEnd,
    cancelEnd,
  }), [
    isOpen, isMinimized, timeLeft, isRunning, sessionType,
    focusDuration, breakDuration, currentCycle, totalCycles,
    mode, soundEnabled, completedSessions,
    isRadioPlaying, volume, isMuted,
    tracks, currentTrack, tracksLoading,
    audioSource, stations, currentStation, stationsLoading,
    needsEndConfirmation,
    openModoForja, minimizeModoForja, expandModoForja, endModoForja,
    toggleTimer, resetTimer, setFocusDuration, setBreakDuration,
    setTotalCycles, setMode, toggleSound, toggleRadio,
    setVolume, setMuted, toggleMute,
    confirmEnd, cancelEnd,
  ]);

  return (
    <ModoForjaContext.Provider value={value}>
      {children}
    </ModoForjaContext.Provider>
  );
}

export function useModoForja() {
  const context = useContext(ModoForjaContext);
  if (context === undefined) {
    throw new Error('useModoForja must be used within a ModoForjaProvider');
  }
  return context;
}
